import Phaser from "phaser";
import type { EntitySnapshot, StateSyncPayload, EntityUpdatePayload } from "@aetheria/shared-game";

// EntitySnapshot/StateSyncPayload/EntityUpdatePayload used to be defined
// locally in this file with a DIFFERENT shape than what world-do actually
// sent (see shared-game/message-types.ts's own comment for the full
// story) — every entity silently failed to render as a result, regardless
// of whether real sprite art existed. Now imported from the canonical
// source both ends actually share.

/**
 * Renders the live zone/entity state coming off the real-time protocol
 * (2.8): a full `state_sync` payload replaces the known entity set; an
 * `entity_update` payload patches one entity (position/hp) or removes it.
 *
 * Deliberately dumb -- it never computes gameplay values itself (1.4:
 * StatCalculator.ts is server-authoritative; this class only ever
 * displays numbers the server already sent).
 */
export class WorldRenderer {
  private sprites = new Map<string, Phaser.GameObjects.Sprite>();
  private nameLabels = new Map<string, Phaser.GameObjects.Text>();
  private hpBars = new Map<string, Phaser.GameObjects.Graphics>();
  private generatedFallbackKeys = new Set<string>();
  // Latest known snapshot per entity — needed so a click handler can
  // report full current data (name/hp/maxHp/kind), not just an id.
  private entityData = new Map<string, EntitySnapshot>();
  private clickHandler: ((entity: EntitySnapshot) => void) | null = null;
  private removeHandler: ((entityId: string) => void) | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Registers a callback fired when the player clicks an entity sprite.
   * Kept as a callback rather than this class reaching into combat/UI
   * state directly — WorldRenderer stays "deliberately dumb" (its own
   * doc comment above): it reports what was clicked, the caller (WorldScene)
   * decides what that means (select as combat target, open a shop, etc).
   */
  onEntityClick(handler: (entity: EntitySnapshot) => void): void {
    this.clickHandler = handler;
  }

  /**
   * Registers a callback fired when an entity is removed (state_sync no
   * longer includes it, or an explicit entity_update {removed: true} —
   * e.g. a defeated monster). Lets WorldScene clear CombatHud's target
   * if the entity that just vanished is the one currently targeted.
   */
  onEntityRemoved(handler: (entityId: string) => void): void {
    this.removeHandler = handler;
  }

  /**
   * No real character/monster/NPC sprite art exists anywhere in this
   * project yet (Team Atelier's own pending-work-writeup.md flags this
   * explicitly — it's blocked on an art-source decision, not built
   * speculatively here). Rather than leave entities invisible until that's
   * resolved, this generates a simple, visually-distinct placeholder
   * texture per entity kind the first time it's needed, and reuses it
   * after that. The moment a real texture matching entity.spriteKey is
   * actually loaded (e.g. once Team Atelier's manifest ships it),
   * textures.exists() picks it up automatically — nothing else in this
   * class needs to change.
   */
  private resolveTextureKey(entity: EntitySnapshot): string {
    if (this.scene.textures.exists(entity.spriteKey)) return entity.spriteKey;

    const fallbackKey = `__fallback_${entity.kind}`;
    if (!this.generatedFallbackKeys.has(fallbackKey)) {
      const colorByKind: Record<EntitySnapshot["kind"], number> = {
        player: 0x2f80b8, // daybreak-cyan-ish blue, matches tokens.css
        npc: 0x5ac97a, // isle-moss green
        mob: 0xe85d4e, // ember-coral red
      };
      const g = this.scene.add.graphics();
      g.fillStyle(colorByKind[entity.kind], 1);
      g.fillCircle(16, 16, 14);
      g.lineStyle(2, 0x0d1420, 1);
      g.strokeCircle(16, 16, 14);
      g.generateTexture(fallbackKey, 32, 32);
      g.destroy();
      this.generatedFallbackKeys.add(fallbackKey);
    }
    return fallbackKey;
  }

  applyStateSync(payload: StateSyncPayload): void {
    const seenIds = new Set(payload.entities.map((e) => e.entityId));

    for (const id of [...this.sprites.keys()]) {
      if (!seenIds.has(id)) this.removeEntity(id);
    }

    for (const entity of payload.entities) {
      this.upsertEntity(entity);
    }
  }

  applyEntityUpdate(update: EntityUpdatePayload): void {
    if (update.removed) {
      this.removeEntity(update.entityId);
      return;
    }

    const sprite = this.sprites.get(update.entityId);
    if (!sprite) return; // update for an entity not seen via state_sync yet; next full sync catches it up

    const existing = this.entityData.get(update.entityId);
    if (existing) {
      this.entityData.set(update.entityId, {
        ...existing,
        x: update.x ?? existing.x,
        y: update.y ?? existing.y,
        hp: update.hp ?? existing.hp,
      });
    }

    if (update.x !== undefined && update.y !== undefined) {
      this.scene.tweens.add({ targets: sprite, x: update.x, y: update.y, duration: 150, ease: "Linear" });
      const label = this.nameLabels.get(update.entityId);
      if (label) this.scene.tweens.add({ targets: label, x: update.x, y: update.y - 20, duration: 150 });
    }

    if (update.hp !== undefined) {
      this.updateHpBar(update.entityId, update.hp);
    }
  }

  private upsertEntity(entity: EntitySnapshot): void {
    this.entityData.set(entity.entityId, entity);

    let sprite = this.sprites.get(entity.entityId);
    const textureKey = this.resolveTextureKey(entity);
    if (!sprite) {
      sprite = this.scene.add.sprite(entity.x, entity.y, textureKey);
      sprite.setInteractive({ useHandCursor: true });
      sprite.on("pointerdown", (_pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: { stopPropagation: () => void }) => {
        // Stop this click from also reaching the scene-level pointerdown
        // handler that sends a "move" — clicking an entity means "target
        // it," not "walk to where I clicked."
        event.stopPropagation();
        const latest = this.entityData.get(entity.entityId);
        if (latest) this.clickHandler?.(latest);
      });
      this.sprites.set(entity.entityId, sprite);

      const label = this.scene.add
        .text(entity.x, entity.y - 20, entity.name, { fontSize: "11px", color: "#ffffff" })
        .setOrigin(0.5);
      this.nameLabels.set(entity.entityId, label);
    } else {
      sprite.setPosition(entity.x, entity.y);
      if (sprite.texture.key !== textureKey) sprite.setTexture(textureKey);
      this.nameLabels.get(entity.entityId)?.setPosition(entity.x, entity.y - 20);
    }

    if (entity.hp !== undefined && entity.maxHp !== undefined) {
      this.updateHpBar(entity.entityId, entity.hp, entity.maxHp);
    }
  }

  private updateHpBar(entityId: string, hp: number, maxHp?: number): void {
    const sprite = this.sprites.get(entityId);
    if (!sprite) return;

    let bar = this.hpBars.get(entityId);
    if (!bar) {
      bar = this.scene.add.graphics();
      this.hpBars.set(entityId, bar);
    }

    const width = 24;
    const ratio = maxHp ? Math.max(0, Math.min(1, hp / maxHp)) : 1;
    bar.clear();
    bar.fillStyle(0x222222, 0.8);
    bar.fillRect(sprite.x - width / 2, sprite.y - 30, width, 4);
    bar.fillStyle(ratio > 0.3 ? 0x4caf50 : 0xe53935, 1);
    bar.fillRect(sprite.x - width / 2, sprite.y - 30, width * ratio, 4);
  }

  private removeEntity(entityId: string): void {
    this.sprites.get(entityId)?.destroy();
    this.sprites.delete(entityId);
    this.nameLabels.get(entityId)?.destroy();
    this.nameLabels.delete(entityId);
    this.hpBars.get(entityId)?.destroy();
    this.hpBars.delete(entityId);
    this.entityData.delete(entityId);
    this.removeHandler?.(entityId);
  }

  destroy(): void {
    for (const id of [...this.sprites.keys()]) this.removeEntity(id);
  }
}
